import type { JSX, ReactNode } from "react";

interface StatsState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

export interface LineageStatsSectionProps<T> {
  /**
   * data-testid mapping (= 既存 testid との互換維持)。
   * spawn-stats の ul testid は `scenario-lineage-panel-stats` (= prefix のみ) で他 5 section
   * と naming convention 不揃いのため、 testIdPrefix で derive せず 3 testid を explicit に渡す。
   */
  testIds: {
    section: string;
    loading: string;
    stats: string;
  };
  /** section header の 文言。 */
  title: string;
  /** loading 中の indicator 文言。 */
  loadingLabel: string;
  /** useStatsFetch hook の return state を受ける。 */
  state: StatsState<T>;
  /** data が non-null のときに ul の中身として render する callback。 */
  renderItems: (data: T) => ReactNode;
}

/**
 * scenario-lineage-panel-split (Phase δ' refactor): ScenarioLineagePanel の 6 stats section
 * 共通 JSX (= section + loading + ul + li) を集約する generic component。
 *
 * 各 stats section が同 shape (= section header + loading indicator + state-dependent ul render)
 * を共有していたため、 testIdPrefix / title / loadingLabel / state / renderItems の 5 props で
 * paramterize、 各 callsite は li 内容 (= renderItems callback) のみ提供。
 *
 * 上流 / 下流 section は性質が異なる (= ScenarioSpawnNotification 呼出 / paginated list +
 * visibility badge) ため本 component に集約しない。
 *
 * data-testid / textContent / Tailwind class は既存 6 section と完全同等で、 既存 component
 * test 12 case が touch ゼロで透過 PASS する。
 */
export function LineageStatsSection<T>({
  testIds,
  title,
  loadingLabel,
  state,
  renderItems,
}: LineageStatsSectionProps<T>): JSX.Element {
  return (
    <section
      data-testid={testIds.section}
      className="flex flex-col gap-1 px-2 py-2 bg-slate-900/40 rounded"
    >
      <h3 className="text-xs text-slate-300/80 font-medium">{title}</h3>
      {state.isLoading ? (
        <p
          data-testid={testIds.loading}
          className="text-xs text-slate-200/60"
        >
          {loadingLabel}
        </p>
      ) : state.error !== null ? null : state.data === null ? null : (
        <ul
          data-testid={testIds.stats}
          className="text-xs flex flex-col gap-0.5 text-slate-100"
        >
          {renderItems(state.data)}
        </ul>
      )}
    </section>
  );
}
